// Sabit Rapor · 5280 — Stok Ekstresi (Detay) (Raporlar → Sabit Raporlar).
//
// İki ekran (Sabit Rapor deseni):
//   1) StockLedgerCriteriaPage — kriter formu. Birim, Distribütör, Depo, Ürün Kodu ve Tarih
//      Aralığı ZORUNLU; Üretici opsiyonel. "Raporu Göster" ayrı pencerede açar.
//   2) StockLedgerListPage — /api/detail-reports/stock-ledger'dan ürünün depo hareketleri
//      (StockMovement) + kümülatif bakiye. Giriş/Çıkış = PhysicalDelta işareti; Matbu No ve
//      Müşteri kaynak belgeden (Invoice/Waybill/StockDocument) çözülür.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams} from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { DateInput } from './reports/DateInput';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/stock-ledger';
const REPORT_CODE = '5280';
const REPORT_TITLE = 'Stok Ekstresi (Detay)';

// ── Yardımcılar ──
function normalizeList(payload) { if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return []; }
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k];
  return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o);
  const name = optName(o);
  if (code && name) return `${code} - ${name}`;
  return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
const nfInt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const fmtNum = (v) => (v === null || v === undefined || v === '' ? '—' : nfInt.format(Number(v) || 0));
const fmtCount = (v) => new Intl.NumberFormat('tr-TR').format(Number(v) || 0);

function today() { const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function firstOfYear() { const d = new Date();
  return `${d.getFullYear()}-01-01`; }
const fmtDate = (iso) => { if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d ? `${d}.${m}.${y}` : iso; };

function useLookups() { const [lk, setLk] = useState({ vendors: [], warehouses: [], products: [], producers: [] });
  useEffect(() => { const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/warehouses/Select').then((warehouses) => setLk((s) => ({ ...s, warehouses })));
    load('/api/products/Select').then((products) => setLk((s) => ({ ...s, products })));
    load('/api/producers/Select').then((producers) => setLk((s) => ({ ...s, producers }))); }, []);
  return lk; }

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = { page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  hTitle: { fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0 },
  hCrumb: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  req: { color: '#dc2626', marginLeft: 3 },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5,
    width: '100%', boxSizing: 'border-box' },
  inputReq: { borderColor: '#fca5a5', background: 'rgba(220,38,38,0.03)' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px',
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  okChip: { marginTop: 8, padding: '8px 16px', borderRadius: 10, background: 'rgba(22,163,74,0.10)', color: '#16a34a', fontWeight: 700, fontSize: 13 },
  warn: { marginTop: 8, padding: '8px 16px', borderRadius: 10, background: 'rgba(217,119,6,0.10)', color: '#b45309', fontWeight: 700, fontSize: 13 } };

function Toggle({ on, onChange }) { return (
    <button type="button" onClick={() => onChange(!on)}
      style={{ width: 46, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 3,
        background: on ? 'var(--accent, #0073ea)' : 'var(--border-color, #cbd5e1)', display: 'flex',
        justifyContent: on ? 'flex-end' : 'flex-start' }}>
      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block' }} />
    </button>
  ); }
function Field({ label, required, children }) { return (
    <label style={c.field}>
      <span style={c.label}>{label}{required && <span style={c.req}>*</span>}</span>
      {children}
    </label>
  ); }
function Select({ value, onChange, options, placeholder = 'Tümü', required, invalid }) { return (
    <select style={{ ...c.input, ...(required && invalid ? c.inputReq : {}) }} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>
          {o.label !== undefined ? o.label : optLabel(o)}
        </option>
      ))}
    </select>
  ); }

const RANGE_PRESETS = [
  { value: 'custom', label: 'İki tarih arası' },
  { value: 'today', label: 'Bugün' },
  { value: 'month', label: 'Bu Ay' },
  { value: 'year', label: 'Bu Yıl' },
];
function presetRange(preset) { const d = new Date();
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  if (preset === 'today') return [iso(d), iso(d)];
  if (preset === 'month') return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, iso(d)];
  if (preset === 'year') return [firstOfYear(), iso(d)];
  return null; }

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function StockLedgerCriteriaPage() { usePageMeta(REPORT_TITLE, "Sabit Raporlar › Stok");
  const lk = useLookups();
  const [showCriteria, setShowCriteria] = useState(false);
  const [unit, setUnit] = useState('Adet');
  const [unitDivisor, setUnitDivisor] = useState('1');
  const [vendorId, setVendorId] = useState([]);
  const [warehouseId, setWarehouseId] = useState([]);
  const [productId, setProductId] = useState([]);
  const [producerId, setProducerId] = useState([]);
  const [rangeType, setRangeType] = useState('year');
  const [from, setFrom] = useState(firstOfYear);
  const [to, setTo] = useState(today);
  const [dynamicDate, setDynamicDate] = useState(false);
  const [mixedCase, setMixedCase] = useState(false);
  const [units, setUnits] = useState([{ name: 'Adet', divisor: 1 }]);

  // Seçili ürünün birimlerini yükle (ProductUnit) — Birim çarpanı miktarları böler.
  useEffect(() => { if (!productId) { setUnits([{ name: 'Adet', divisor: 1 }]); setUnit('Adet'); setUnitDivisor('1'); return; }
    fetch(`/api/productunits/ForProduct?productId=${productId}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(normalizeList)
      .then((rows) => { const us = rows.map((u) => ({ name: pick(u, 'unitName', 'UnitName') || 'Birim', divisor: Number(pick(u, 'multiplier', 'Multiplier')) || 1, isBase: pick(u, 'isBase', 'IsBase') }));
        const list = us.length ? us : [{ name: 'Adet', divisor: 1 }];
        setUnits(list);
        const base = list.find((u) => u.isBase) || list[0];
        setUnit(base.name); setUnitDivisor(String(base.divisor)); })
      .catch(() => { setUnits([{ name: 'Adet', divisor: 1 }]); setUnit('Adet'); setUnitDivisor('1'); }); }, [productId]);

  // Ürün listesi seçili distribütöre bağlı: o distribütörün ürünleri + global (VendorId boş) ürünler.
  const productOptions = useMemo(() => { const list = vendorId
      ? lk.products.filter((p) => { const pv = pick(p, 'vendorId', 'VendorId'); return !pv || String(pv) === String(vendorId); })
      : lk.products;
    return list.map((p) => ({ value: String(optId(p)), label: optLabel(p) })); }, [lk.products, vendorId]);
  const unitOptions = units.map((u) => ({ value: u.name, label: u.name }));

  const onRangeType = (t) => { setRangeType(t);
    const r = presetRange(t);
    if (r) { setFrom(r[0]); setTo(r[1]); } };
  const onUnit = (name) => { setUnit(name);
    const u = units.find((x) => x.name === name);
    setUnitDivisor(String(u?.divisor || 1)); };

  // Birim/Distribütör/Depo/Ürün "Tümü" (boş) bırakılabilir; yalnız Tarih Aralığı zorunlu.
  const ready = !!(from && to);
  const missing = [];
  if (!from || !to) missing.push('Tarih Aralığı');

  const clear = () => { setVendorId([]); setWarehouseId([]); setProductId([]); setProducerId([]); setRangeType('year'); setFrom(firstOfYear()); setTo(today()); };

  const showReport = () => { if (!ready) return;
    const p = new URLSearchParams();
    p.set('vendorId', vendorId);
    p.set('warehouseId', warehouseId);
    p.set('productId', productId);
    p.set('from', from);
    p.set('to', to);
    if (unitDivisor && unitDivisor !== '1') p.set('unitDivisor', unitDivisor);
    p.set('unitName', unit);
    if (producerId.length) p.set('producerId', producerId.join(','));
    if (showCriteria) p.set('showCriteria', '1');
    p.set('embed', 'true');
    const w = Math.min(1680, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/stock-ledger-list?${p.toString()}`, 'stokEkstresi5280',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`); };

  return (
    <div style={c.page}>

      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Filtreleri Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary, opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }} onClick={showReport} disabled={!ready}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.toggleRow}>
          <Toggle on={showCriteria} onChange={setShowCriteria} />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>Girilen kriterleri raporda göster</span>
        </div>

        <div style={c.grid}>
          <Field label="Birimler">
            <MultiSelect value={unit} onChange={onUnit} options={unitOptions} />
          </Field>
          <Field label="Distribütör">
            <MultiSelect value={vendorId} onChange={(v) => { setVendorId(v); setProductId([]); }} options={asChoices(lk.vendors)} />
          </Field>
          <Field label="Depo">
            <MultiSelect value={warehouseId} onChange={setWarehouseId} options={asChoices(lk.warehouses)} />
          </Field>
          <Field label="Ürün Kodu">
            <MultiSelect value={productId} onChange={setProductId} options={productOptions} />
          </Field>
        </div>

        <div style={c.sectionSep}><span>TARİH ARALIĞI</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Aralık">
            <Select value={rangeType} onChange={onRangeType} options={RANGE_PRESETS} placeholder="İki tarih arası" />
          </Field>
          <Field label="Başlangıç" required>
            <DateInput value={from} onChange={(v) => { setFrom(v); setRangeType('custom'); }} />
          </Field>
          <Field label="Bitiş" required>
            <DateInput value={to} onChange={(v) => { setTo(v); setRangeType('custom'); }} />
          </Field>
          <Field label="Dinamik Tarih">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44 }}>
              <Toggle on={dynamicDate} onChange={setDynamicDate} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)' }}>{dynamicDate ? 'Açık' : 'Kapalı'}</span>
            </div>
          </Field>
        </div>

        <div style={c.sectionSep}><span>EK FİLTRELER</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Üretici">
            <MultiSelect value={producerId} onChange={setProducerId} options={asChoices(lk.producers)} />
          </Field>
          <Field label="&nbsp;">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44 }}>
              <Toggle on={mixedCase} onChange={setMixedCase} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)' }}>Karma Koli Detaylarını Dikkate Alma</span>
            </div>
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 620 }}>
            Yalnız <strong>Tarih Aralığı</strong> zorunlu; Birim, Distribütör, Depo, Ürün <strong>Tümü</strong> bırakılabilir
            (hepsini Tümü seçersen tüm hareketler listelenir). <strong>Raporu Göster</strong>’e basın — depo hareketleri
            (giriş / çıkış / bakiye) tam ekran açılır. Not: bakiye kümülatifi yalnız tek ürün + depo seçiliyken anlamlıdır.
          </span>
          {ready
            ? <div style={c.okChip}>✓ Zorunlu alanlar tamam · rapor hazır</div>
            : <div style={c.warn}>⚠ Eksik: {missing.join(', ')}</div>}
        </div>
      </div>
    </div>
  ); }

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
// Yön chip'i (Giriş/Çıkış/Transfer) — ReportGrid `cell` render'ı olarak kullanılır.
function dirTone(v) { if (v === 'Giriş') return { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a' };
  if (v === 'Çıkış') return { bg: 'rgba(220,38,38,0.10)', fg: '#dc2626' };
  if (v === 'Transfer') return { bg: 'rgba(0,115,234,0.10)', fg: 'var(--accent)' };
  return { bg: 'var(--bg-input, #f1f5f9)', fg: '#64748b' }; }
function DirChip({ v }) { if (!v) return '—';
  const t = dirTone(v);
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: t.bg, color: t.fg, fontWeight: 700, fontSize: 12 }}>{v}</span>; }
function CodeChip({ v }) { if (!v) return '—';
  return <span style={{ padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 700, fontSize: 12 }}>{v}</span>; }
// Giriş/Çıkış — sabit renkli sayısal hücre (değer yoksa renksiz "—").
function NumTone({ v, color, bold }) { const has = v !== null && v !== undefined && v !== '';
  return <span style={{ color: has ? color : undefined, fontWeight: bold ? 800 : 600 }}>{fmtNum(v)}</span>; }

// Kolon sözleşmesi: sticky (Depo Adı) sola donuk; sayısal kolonlar sağa hizalı + toplanır.
// Filtre/sıralama/gruplama/sayfalama ReportGrid (TanStack) içinde — eski client-side
// rowMatches/pageWindow/PAGE_SIZES/GROUP_OPTIONS kaldırıldı.
const COLUMNS = [
  { key: 'warehouseName', label: 'Depo Adı', bold: true, sticky: true },
  { key: 'urun', label: 'Ürün' },
  { key: 'sira', label: 'Sıra', num: true, fmt: fmtNum, agg: 'none' },
  { key: 'islemTarihi', label: 'İşlem Tarihi', date: true, fmt: fmtDate },
  { key: 'matbuNo', label: 'Matbu No' },
  { key: 'islemTipi', label: 'İşlem Tipi' },
  { key: 'depoHareket', label: 'Depo Hareket', cell: (row) => <DirChip v={row.depoHareket} /> },
  { key: 'musteriKod', label: 'Müşteri Kodu', cell: (row) => <CodeChip v={row.musteriKod} /> },
  { key: 'musteriUnvan', label: 'Müşteri Ünvanı' },
  { key: 'giris', label: 'Giriş', num: true, fmt: fmtNum, cell: (row) => <NumTone v={row.giris} color="#16a34a" /> },
  { key: 'cikis', label: 'Çıkış', num: true, fmt: fmtNum, cell: (row) => <NumTone v={row.cikis} color="#dc2626" /> },
  { key: 'bakiye', label: 'Bakiye', num: true, bold: true, fmt: fmtNum, agg: 'none', cell: (row) => <NumTone v={row.bakiye} bold /> },
];

const l = { wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)',
    position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
    border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #f8fafc)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13, cursor: 'pointer' },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap',
    background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  groupSel: { height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13, padding: '0 8px', cursor: 'pointer' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, padding: '14px 20px 4px' },
  kpiCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)' },
  kpiIcon: (bg, fg) => ({ width: 40, height: 40, borderRadius: 11, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }),
  kpiLabel: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary, #64748b)' },
  kpiValue: { fontSize: 20, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  tableScroll: { flex: 1, overflow: 'auto', padding: '10px 20px 20px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card, #fff)' },
  th: { position: 'sticky', top: 0, background: 'var(--bg-card, #f8fafc)', textAlign: 'left',
    padding: '11px 12px', fontWeight: 800, color: 'var(--text-secondary, #64748b)', fontSize: 11.5,
    borderBottom: '2px solid var(--border-color, #e5e7eb)', whiteSpace: 'nowrap', zIndex: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border-color, #eef2f7)', whiteSpace: 'nowrap', color: 'var(--text-primary, #0f172a)' },
  idx: { width: 34, textAlign: 'right', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  groupRow: { background: 'var(--bg-input, #f1f5f9)', fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '12px 20px', background: 'var(--bg-card, #fff)', borderTop: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', bottom: 0 },
  thInner: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' },
  funnelBtn: (active) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
    borderRadius: 6, cursor: 'pointer', padding: 0,
    border: `1px solid ${active ? 'var(--accent, #0073ea)' : 'var(--border-color, #e5e7eb)'}`,
    background: active ? 'var(--accent, #0073ea)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-secondary, #94a3b8)' }),
  codeChip: { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 700, fontSize: 12 },
  backdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  popover: { position: 'fixed', zIndex: 41, width: 250, padding: 12, borderRadius: 12,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', gap: 10 },
  popTitle: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)' },
  popInput: { padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  popBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  popBtn: (primary) => ({ padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
    background: primary ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-input, #f1f5f9)',
    color: primary ? '#fff' : 'var(--text-secondary, #475569)' }),
  pageBtn: (active, disabled) => ({ minWidth: 34, height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)',
    background: active ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: '0 8px' }),
  pageSizeSel: { height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13, padding: '0 8px', marginLeft: 8, cursor: 'pointer' } };

function KpiCard({ label, value, bg, fg, icon }) { return (
    <div style={l.kpiCard}>
      <span style={l.kpiIcon(bg, fg)}>{icon}</span>
      <div><div style={l.kpiLabel}>{label}</div><div style={l.kpiValue}>{value}</div></div>
    </div>
  ); }

const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function printAsPdf({ rows, subtitle }) { const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row) => { const tds = COLUMNS.map((col) => { const v = col.num ? fmtNum(row[col.key]) : (col.date ? fmtDate(row[col.key]) : row[col.key]);
      return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`; }).join('');
    return `<tr>${tds}</tr>`; }).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title>
<style>
  *{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:16px}
  .rhead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1e3a8a;padding-bottom:8px;margin-bottom:6px}
  .rhead h1{font-size:15px;margin:0;color:#1e3a8a}.rhead .meta{font-size:10px;color:#64748b;text-align:right}
  .crit{font-size:10px;color:#475569;margin:6px 0 10px}
  table{width:100%;border-collapse:collapse}
  th{background:#eef2f7;color:#475569;font-size:9px;text-transform:uppercase;padding:5px 6px;border-bottom:1.5px solid #cbd5e1}
  td{font-size:10px;padding:4px 6px;border-bottom:1px solid #eef2f7;white-space:nowrap}
  tr:nth-child(even) td{background:#fafcff}@page{size:A4 landscape;margin:12mm}
</style></head><body>
  <div class="rhead"><h1>${REPORT_TITLE}</h1><div class="meta">${rows.length} hareket</div></div>
  <div class="crit"><strong>Kriterler:</strong> ${hesc(subtitle)}</div>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); }; }

export function StockLedgerListPage() { usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lk = useLookups();

  const params = useMemo(() => { const o = {};
    for (const k of ['vendorId', 'warehouseId', 'productId', 'from', 'to', 'producerId', 'unitDivisor']) { const v = sp.get(k);
      if (v) o[k] = v; }
    return o; }, [sp]);
  const unitName = sp.get('unitName') || 'Adet';

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filtre/sıralama/gruplama/sayfalama ReportGrid'in içinde (kalıcı görünüm). Excel/PDF
  // dışa aktarımı kullanıcının GÖRDÜĞÜ kümeyi almalı → grid görünen satırları buraya bildirir.
  // null = grid henüz bildirmedi (Excel ham `rows`'a düşer).
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => { setLoading(true); setError(null);
    try { const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null); } catch (e) { setError(e.message || 'Hata'); setRows([]); } finally { setLoading(false); } }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Grid görünen satırları bildirir (filtre + sıralama uygulanmış); Excel/PDF bunu kullanır.
  const exportRows = visibleRows ?? rows;

  const nameById = (arr, id) => { const o = arr.find((x) => String(optId(x)) === String(id)); return o ? optName(o) : id; };
  const vendorName = useMemo(() => (params.vendorId ? nameById(lk.vendors, params.vendorId) : 'Tümü'), [lk.vendors, params.vendorId]);
  const warehouseName = useMemo(() => (params.warehouseId ? nameById(lk.warehouses, params.warehouseId) : 'Tümü'), [lk.warehouses, params.warehouseId]);
  const productName = useMemo(() => { if (!params.productId) return 'Tümü'; const p = lk.products.find((x) => String(optId(x)) === String(params.productId)); return p ? optName(p) : params.productId; }, [lk.products, params.productId]);
  const summaryText = [`Birim: ${unitName}`, `Distribütör: ${vendorName}`, `Depo: ${warehouseName}`, `Ürün: ${productName}`, `Tarih: ${fmtDate(params.from)} – ${fmtDate(params.to)}`].join(' · ');

  const onExcel = () => { const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => (col.date ? fmtDate(row[col.key]) : row[col.key]) }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_stok_ekstresi_detay`); };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>{loading ? 'Yükleniyor…' : `${productName} · ${fmtCount(rows.length)} hareket · ${nowLabel}`}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v6" /><path d="M15.5 12H14v6" /><path d="M14 15h1.5" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #94a3b8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Birim: {unitName}</span>
        <span style={l.critChip}>Distribütör: {vendorName}</span>
        <span style={l.critChip}>Depo: {warehouseName}</span>
        <span style={l.critChip}>Ürün: {productName}</span>
        <span style={l.critChip}>Tarih: {fmtDate(params.from)} – {fmtDate(params.to)}</span>
      </div>

      {totals && (
        <div style={l.kpiRow}>
          <KpiCard label="Toplam Giriş" value={fmtNum(totals.toplamGiris)} bg="rgba(22,163,74,0.10)" fg="#16a34a"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></svg>} />
          <KpiCard label="Toplam Çıkış" value={fmtNum(totals.toplamCikis)} bg="rgba(220,38,38,0.10)" fg="#dc2626"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></svg>} />
          <KpiCard label="Son Bakiye" value={fmtNum(totals.sonBakiye)} bg="rgba(0,115,234,0.10)" fg="var(--accent)"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>} />
          <KpiCard label="Hareket Adedi" value={fmtCount(totals.hareketAdedi)} bg="rgba(124,58,237,0.10)" fg="#7c3aed"
            icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>} />
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="stock-ledger"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  ); }
